import { QueryKey, useQuery } from "@tanstack/react-query";
import { Schema, Either } from "effect";
import { TreeFormatter } from "effect/ParseResult";
import { getSanityAuthToken } from "./getSanityAuthToken";
import { HttpError } from "./useSanityImportApi";

type FetchArgs = {
  cruiseCode?: string;
  silverseaApiRootUrl: string;
  action: string;
  headers?: Record<string, string>;
};

export const fetchFun = async <T>(
  url: string,
  headers?: Record<string, string>
): Promise<T> => {
  const token = getSanityAuthToken();
  if (!token) {
    throw new HttpError(
      401,
      "Authentication required. Please log in to Sanity Studio."
    );
  }

  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...headers
    }
  });

  if (!response.ok) {
    throw new HttpError(
      response.status,
      `HTTP ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

const fetchDiagnostic = async <T>({
  cruiseCode,
  silverseaApiRootUrl,
  action,
  headers
}: FetchArgs): Promise<T> => {
  const url = new URL(action, silverseaApiRootUrl);
  if (cruiseCode) url.searchParams.append("cruiseCode", cruiseCode);

  return fetchFun<T>(url.toString(), headers);
};

type Args<T> = {
  queryKey: QueryKey;
  enabled: boolean;
  action: string;
  silverseaApiRootUrl: string;
  cruiseCode?: string;
  type?: Schema.Schema<T>;
  headers?: Record<string, string>;
};

export const useDiagnosticApi = <T>(props: Args<T>) => {
  const {
    queryKey,
    enabled,
    action,
    cruiseCode,
    silverseaApiRootUrl,
    type,
    headers
  } = props;

  return useQuery({
    queryKey: queryKey,
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchDiagnostic<T>({
        cruiseCode,
        silverseaApiRootUrl,
        action,
        headers
      }).then((data) => {
        if (!type) return data;

        return Schema.decodeEither(type)(data).pipe(
          Either.match({
            onLeft: (error) => {
              throw new Error(
                `Decode error in ${action}: ${TreeFormatter.formatError(error)}`
              );
            },
            onRight: (a) => a
          })
        );
      })
  });
};
